import React from 'react';
import './Dashboard.css';

// Custom semi-circle (half-circle) gauge component
const SemiCircleGauge = ({ value, size = 180, strokeWidth = 14, color, children }) => {
  const radius = (size - strokeWidth) / 2;
  const circumference = radius * Math.PI; // Half circle
  const offset = circumference - (value / 100) * circumference;
  
  const colorMap = {
    orange: '#ff7a00',
    blue: '#00d4ff',
    emerald: '#10b981',
    coral: '#f97316'
  };
  
  const strokeColor = colorMap[color] || '#ff7a00';
  
  // Generate tick marks - dense at edges, sparse at peak
  const ticks = [];
  let currentAngle = 0;
  let tickIndex = 0;
  
  while (currentAngle <= Math.PI) {
    // Calculate spacing based on position - tight at edges (0 and PI), wider at middle (PI/2)
    const normalizedPosition = Math.abs(currentAngle - Math.PI / 2) / (Math.PI / 2); // 0 at center, 1 at edges
    const minSpacing = 0.03; // Tight spacing at edges
    const maxSpacing = 0.15; // Wide spacing at peak
    const spacing = maxSpacing - (maxSpacing - minSpacing) * normalizedPosition;
    
    // Calculate thickness - thicker at edges, thinner in middle
    const minWidth = 0.8;
    const maxWidth = 2;
    const tickWidth = minWidth + (maxWidth - minWidth) * normalizedPosition;
    
    const tickLength = strokeWidth - 4;
    const outerRadius = radius + strokeWidth / 2 - 2;
    const innerRadius = outerRadius - tickLength;
    
    const x1 = size / 2 + outerRadius * Math.cos(Math.PI - currentAngle);
    const y1 = size / 2 - outerRadius * Math.sin(Math.PI - currentAngle);
    const x2 = size / 2 + innerRadius * Math.cos(Math.PI - currentAngle);
    const y2 = size / 2 - innerRadius * Math.sin(Math.PI - currentAngle);
    
    ticks.push(
      <line
        key={tickIndex}
        x1={x1}
        y1={y1}
        x2={x2}
        y2={y2}
        stroke="rgba(255, 255, 255, 0.2)"
        strokeWidth={tickWidth}
        strokeLinecap="round"
      />
    );
    
    currentAngle += spacing;
    tickIndex++;
  }
  
  return (
    <div style={{ position: 'relative', width: size, height: size / 2 + 40 }}>
      <svg width={size} height={size / 2 + 20} style={{ overflow: 'visible' }}>
        {/* Background semi-circle */}
        <path
          d={`M ${strokeWidth / 2} ${size / 2} A ${radius} ${radius} 0 0 1 ${size - strokeWidth / 2} ${size / 2}`}
          fill="none"
          stroke="rgba(255, 255, 255, 0.1)"
          strokeWidth={strokeWidth}
          strokeLinecap="butt"
        />
        
        {/* Tick marks - drawn on top of background */}
        {ticks}
        
        {/* Progress semi-circle - will cover tick marks where it fills */}
        <path
          d={`M ${strokeWidth / 2} ${size / 2} A ${radius} ${radius} 0 0 1 ${size - strokeWidth / 2} ${size / 2}`}
          fill="none"
          stroke={strokeColor}
          strokeWidth={strokeWidth}
          strokeDasharray={circumference}
          strokeDashoffset={offset}
          strokeLinecap="butt"
          style={{
            transition: 'stroke-dashoffset 1s ease',
            filter: `drop-shadow(0 0 4px ${strokeColor}40)`
          }}
        />
      </svg>
      <div style={{
        position: 'absolute',
        bottom: '0',
        left: 0,
        right: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        {children}
      </div>
    </div>
  );
};

function PerformanceGauges({ metrics }) {
  console.log('PerformanceGauges rendering with metrics:', metrics);
  
  if (!metrics) {
    console.log('No metrics provided to PerformanceGauges');
    return <div style={{ color: 'white', padding: '20px' }}>No metrics available</div>;
  }
  
  // Calculate percentages toward goals
  const calculateProgress = (actual, goal) => {
    if (!goal || goal === 0) return 0;
    return Math.min(Math.round((actual / goal) * 100), 100);
  };

  const gauges = [
    {
      title: 'Sales Target',
      value: metrics.salesRevenue || 0,
      goal: 10000000, // $10M goal
      unit: '$',
      format: (val) => `$${(val / 1000000).toFixed(1)}M`,
      color: 'orange'
    },
    {
      title: 'Service Target',
      value: metrics.serviceRevenue || 0,
      goal: 2000000, // $2M goal
      unit: '$',
      format: (val) => `$${(val / 1000000).toFixed(1)}M`,
      color: 'blue'
    },
    {
      title: 'F&I Penetration',
      value: metrics.fiCloseRate || 0,
      goal: 1000000, // $1M goal for F&I income
      unit: '$',
      format: (val) => `$${(val / 1000).toFixed(0)}k`,
      color: 'emerald'
    },
    {
      title: 'Units Sold',
      value: metrics.partsSold || 0,
      goal: 500, // 500 units goal
      unit: '',
      format: (val) => Math.round(val),
      color: 'coral'
    }
  ];

  return (
    <div className="gauges-container">
      {gauges.map((gauge, idx) => {
        const progress = calculateProgress(gauge.value, gauge.goal);
        
        return (
          <div key={idx} className="gauge-card">
            <h3 className="gauge-title">{gauge.title}</h3>
            <div className="gauge-wrapper">
              <SemiCircleGauge 
                value={progress} 
                size={180}
                strokeWidth={14}
                color={gauge.color}
              >
                <div className="gauge-center">
                  <div className="gauge-percentage">{progress}%</div>
                  <div className="gauge-label">to goal</div>
                </div>
              </SemiCircleGauge>
            </div>
            <div className="gauge-details">
              <div className="gauge-actual">
                <span className="gauge-detail-label">Current</span>
                <span className="gauge-detail-value">{gauge.format(gauge.value)}</span>
              </div>
              <div className="gauge-goal">
                <span className="gauge-detail-label">Goal</span>
                <span className="gauge-detail-value">{gauge.format(gauge.goal)}</span>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

export default PerformanceGauges;
